import React, { useState } from 'react';
import { Card, Segmented } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { SectionHeader } from '~analytics-components/section-header';
import { KpiCardFromSpec } from '~containers/analytics/kpis/kpi-card-from-spec';
import type {
    KpiCampaignContext,
    KpiSegment,
    ResolvedPeriod,
    ResolvedRegion,
} from '~containers/analytics/kpis/types';
import {
    CUSTOMER_GROUPS,
    CUSTOMER_GROUP_CARDS,
    DEFAULT_CUSTOMER_GROUP,
} from './customer-groups';
import { MilestoneChoiceBars } from './milestone-choice-bars';
import { useCampaignMilestoneChoices } from './use-campaign-milestone-choices';
import type { CampaignTierLadderState } from './use-campaign-tier-ladder';
import style from './style.module.scss';

export interface CustomerGroupBlockProps {
    pageRange: ResolvedPeriod;
    pageRegion: ResolvedRegion;
    compareEnabled: boolean;
    waveKey: string;
    campaign: KpiCampaignContext;
    /** The page's own ladder read, shared with the rewards inventory table
     * rather than fetched a second time here. */
    tierLadder: CampaignTierLadderState;
}

const GROUP_OPTIONS = CUSTOMER_GROUPS.map(({ segment, label }) => ({
    label,
    value: segment,
}));

const REGION_NOTE_TITLE = 'Filter by region before comparing coupons';
const REGION_NOTE_BODY =
    'Coupon availability and stock vary by region. Filtering to a single region shows which milestone options were offered to the same members. With multiple regions selected these percentages merge several pools of milestone options into one.';

const RegionNote: React.FC = () => (
    <div className={style.region_note}>
        <InfoCircleOutlined className={style.region_note_icon} aria-hidden />
        <div>
            <p className={style.region_note_title}>{REGION_NOTE_TITLE}</p>
            <p className={style.region_note_body}>{REGION_NOTE_BODY}</p>
        </div>
    </div>
);

/**
 * Choice ratios per customer group: the group selector, the four cards that
 * describe the chosen group, and one bar per milestone of the reward ladder.
 *
 * The selector drives this block alone. The count cards, the rate cards and the
 * inventory table above stay on the whole campaign population, so the group
 * lives in this component's state rather than in the page filter or the URL.
 *
 * The milestone bars read a standing endpoint that takes no period, so they
 * follow the group and the region and are untouched by the page's Period.
 */
export const CustomerGroupBlock: React.FC<CustomerGroupBlockProps> = ({
    pageRange,
    pageRegion,
    compareEnabled,
    waveKey,
    campaign,
    tierLadder,
}) => {
    const [segment, setSegment] = useState<KpiSegment>(DEFAULT_CUSTOMER_GROUP);
    const choices = useCampaignMilestoneChoices(segment, pageRegion);
    const groupLabel =
        CUSTOMER_GROUPS.find((group) => group.segment === segment)?.label ??
        segment;

    return (
        <section className={style.section}>
            <SectionHeader
                title="Choice ratios per customer group"
                action={
                    <Segmented
                        options={GROUP_OPTIONS}
                        value={segment}
                        onChange={(value) => setSegment(value as KpiSegment)}
                    />
                }
            />
            <Card className={style.group_card}>
                <h3 className={style.group_title}>{groupLabel}</h3>
                <div className={style.dashboard_grid}>
                    {CUSTOMER_GROUP_CARDS.map((spec) => (
                        <KpiCardFromSpec
                            key={spec.title}
                            spec={spec}
                            pageRange={pageRange}
                            pageRegion={pageRegion}
                            descriptionOnlyInfo
                            compareEnabled={compareEnabled}
                            waveKey={waveKey}
                            campaign={campaign}
                            segment={segment}
                        />
                    ))}
                </div>
                <MilestoneChoiceBars
                    tierLadder={tierLadder}
                    choices={choices}
                />
                <RegionNote />
            </Card>
        </section>
    );
};
